import { getBearerToken } from "../../../api/postCall/bearerToken";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { logDataToConsole, logError } from "../../../utils/customLogger";
import type { SupportTicketApiComment } from "../supportTicketTypes";
import { parseSupportTicketApiError } from "../utils/parseSupportTicketApiError";

/**
 * One already-uploaded file to send with a comment.
 */
export interface AddSupportTicketCommentAttachment {
  filename: string;
  gcs_path: string;
  content_type: string;
  size: number;
}

/**
 * Request body for POST /pharmacy/support-tickets/add-comment.
 */
export interface AddSupportTicketCommentParams {
  org_id: string;
  ticket_id: string;
  message: string;
  attachments?: AddSupportTicketCommentAttachment[];
}

/**
 * Response returned by POST /pharmacy/support-tickets/add-comment.
 */
export interface AddSupportTicketCommentResponse extends SupportTicketApiComment {}

/**
 * Adds a comment to the selected support ticket.
 *
 * @param params - Organization id, ticket id, and comment message.
 * @returns Created comment from the API.
 */
export const addSupportTicketComment = async (
  params: AddSupportTicketCommentParams,
): Promise<AddSupportTicketCommentResponse> => {
  const body = {
    org_id: params.org_id,
    ticket_id: params.ticket_id,
    message: params.message.trim(),
    attachments: params.attachments ?? [],
  };

  try {
    const token = await getBearerToken();
    const response = await fetch(API_ENDPOINTS.ADD_SUPPORT_TICKET_COMMENT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(
        await parseSupportTicketApiError(response, "Failed to add comment"),
      );
    }

    const result = (await response.json()) as AddSupportTicketCommentResponse;
    logDataToConsole("addSupportTicketComment", result);
    return result;
  } catch (error) {
    logError(error as Error, "Error in addSupportTicketComment");
    throw error;
  }
};
